import React from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Chip,
  Checkbox,
  TextFieldProps,
  SelectProps,
} from "@mui/material";

const FONT = "0.75rem";

export const FilterGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Grid container spacing={1.5} alignItems="flex-end" sx={{ mb: 1 }}>
    {React.Children.map(children, (child) =>
      child ? (
        <Grid item xs={12} sm={6} md={4}>
          {child}
        </Grid>
      ) : null
    )}
  </Grid>
);

// ── Text field ───────────────────────────────────────────────────────────────
export const FilterTextField: React.FC<TextFieldProps> = (props) => (
  <TextField
    fullWidth
    size="small"
    variant="outlined"
    {...props}
    InputProps={{ style: { fontSize: FONT }, ...props.InputProps }}
    InputLabelProps={{ style: { fontSize: FONT }, ...props.InputLabelProps }}
  />
);

// ── Select ───────────────────────────────────────────────────────────────────
type FilterSelectProps = SelectProps & {
  label: string;
  labelId: string;
  children: React.ReactNode;
};

export const FilterSelect: React.FC<FilterSelectProps> = ({
  label,
  labelId,
  children,
  ...props
}) => (
  <FormControl fullWidth variant="outlined" size="small">
    <InputLabel id={labelId} sx={{ fontSize: FONT }}>
      {label}
    </InputLabel>
    <Select
      labelId={labelId}
      label={label}
      sx={{ fontSize: FONT, ...((props.sx as object) ?? {}) }}
      {...props}
    >
      {children}
    </Select>
  </FormControl>
);

// ── Shared assignment option type ────────────────────────────────────────────
export type AssignmentOption = {
  _id: string;
  organization?: { name: string };
  application_type: string;
  assigned_at: string;
  ended_at: string | null;
  is_active: boolean;
};

type AssignmentMultiSelectProps = {
  label?: string;
  value: string[];
  onChange: (value: string[]) => void;
  assignments: AssignmentOption[];
};

export const AssignmentMultiSelect: React.FC<AssignmentMultiSelectProps> = ({
  label = "მონიტორინგები",
  value,
  onChange,
  assignments,
}) => (
  <FormControl fullWidth variant="outlined" size="small">
    <InputLabel shrink sx={{ fontSize: FONT }}>{label}</InputLabel>
    <Select
      multiple
      notched
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value as string[])}
      sx={{ fontSize: FONT }}
      renderValue={(selected) => {
        const ids = selected as string[];
        if (ids.length === 0) return <em style={{ fontSize: FONT }}>ყველა</em>;
        return ids
          .map((id) => {
            const a = assignments.find((x) => x._id === id);
            return a ? `${a.organization?.name} (${a.application_type.toUpperCase()})` : id;
          })
          .join(", ");
      }}
    >
      {[...assignments].sort((a, b) => Number(b.is_active) - Number(a.is_active)).map((a) => (
        <MenuItem key={a._id} value={a._id} sx={{ fontSize: FONT, display: "flex", alignItems: "center", gap: 1 }}>
          <Checkbox checked={value.includes(a._id)} size="small" sx={{ p: 0.5 }} />
          <Box component="span" sx={{ fontSize: FONT, flex: 1 }}>
            {a.organization?.name} — {a.application_type.toUpperCase()}{" "}
            ({new Date(a.assigned_at).toLocaleDateString("ka-GE")}
            {a.ended_at ? ` → ${new Date(a.ended_at).toLocaleDateString("ka-GE")}` : " →"})
          </Box>
          <Chip
            label={a.is_active ? "აქტიური" : "დასრულებული"}
            size="small"
            color={a.is_active ? "success" : "default"}
            sx={{ height: 16, fontSize: "0.6rem", "& .MuiChip-label": { px: 0.75 } }}
          />
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

// ── Assignment single-select (for dialogs/forms) ─────────────────────────────
type AssignmentSelectProps = {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  assignments: AssignmentOption[];
  error?: boolean;
};

export const AssignmentSelect: React.FC<AssignmentSelectProps> = ({
  label = "მონიტორინგი",
  value,
  onChange,
  assignments,
  error,
}) => (
  <FormControl fullWidth variant="outlined" size="small" error={error}>
    <InputLabel sx={{ fontSize: FONT }}>{label}</InputLabel>
    <Select
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value as string)}
      sx={{ fontSize: FONT }}
    >
      {assignments.map((a) => (
        <MenuItem key={a._id} value={a._id} sx={{ fontSize: FONT, display: "flex", alignItems: "center", gap: 1 }}>
          <Box component="span" sx={{ fontSize: FONT, flex: 1 }}>
            {a.organization?.name} — {a.application_type?.toUpperCase()}{" "}
            ({new Date(a.assigned_at).toLocaleDateString("ka-GE")}
            {a.ended_at ? ` → ${new Date(a.ended_at).toLocaleDateString("ka-GE")}` : " →"})
          </Box>
          <Chip
            label={a.is_active ? "აქტიური" : "დასრულებული"}
            size="small"
            color={a.is_active ? "success" : "default"}
            sx={{ height: 16, fontSize: "0.6rem", "& .MuiChip-label": { px: 0.75 } }}
          />
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

export const FilterMenuItem: React.FC<React.ComponentProps<typeof MenuItem>> = ({
  children,
  ...props
}) => (
  <MenuItem sx={{ fontSize: FONT }} {...props}>
    {children}
  </MenuItem>
);
